import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Shield,
    Plus,
    Search,
    X,
    Calendar,
    DollarSign,
    Percent,
    User,
    Building,
    FileText,
    AlertCircle,
    CheckCircle,
    Loader2,
    ArrowLeft,
    Users,
    BookOpen,
    Info,
    Sparkles,
    TrendingUp,
    Award,
    Lock,
    ChevronRight,
} from 'lucide-react';
import api from '../../services/api';

/* =====================================================================
   OpenCoverCreate — Annual Marine Open Cover
   World-class UI: emerald/forest palette, glassy cards, subtle motion.
   ===================================================================== */

export default function OpenCoverCreate() {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [form, setForm] = useState({
        client_name: '',
        client_id: '',
        sum_insured_limit: '',
        currency: 'USD',
        marine_rate: '0.20',
        war_rate: '0.05',
        start_date: '',
        end_date: '',
        terms_conditions: '',
        intermediary_id: null,
        created_by_role: '',
        created_by: null,
        broker_id: null,
        status: 'active',
        is_renewable: 1,
        renewal_count: 0,
        used_amount: 0,
        quote_count: 0,
    });
    const [clients, setClients] = useState([]);
    const [clientSearch, setClientSearch] = useState('');
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [searchingClients, setSearchingClients] = useState(false);

    // Client creation states
    const [showAddClientModal, setShowAddClientModal] = useState(false);
    const [newClient, setNewClient] = useState({
        full_name: '',
        email: '',
        phone: '',
        company_name: '',
        kra_pin: '',
        pin: '',
        role: 'direct_client',
        password: 'Temp123!@#',
    });
    const [savingClient, setSavingClient] = useState(false);
    const [clientError, setClientError] = useState('');
    const [clientSuccess, setClientSuccess] = useState('');

    const searchWrapperRef = useRef(null);

    // Set intermediary_id and other fields from logged-in user
    useEffect(() => {
        if (user?.id) {
            const uid = Number(user.id);
            const safeUid = Number.isFinite(uid) ? uid : user.id;
            setForm(prev => ({
                ...prev,
                intermediary_id: safeUid,
                created_by_role: user?.role === 'agent' ? 'agent' : user?.role === 'broker' ? 'broker' : 'agent',
                created_by: safeUid,
                broker_id: user?.role === 'broker' ? safeUid : null,
            }));
        }
    }, [user]);

    // Fetch clients (users with role 'direct_client') when search term changes
    useEffect(() => {
        if (clientSearch.length < 2) {
            setClients([]);
            setSearchingClients(false);
            return;
        }
        let cancelled = false;
        setSearchingClients(true);
        const fetchClients = async () => {
            try {
                const { data } = await api.get(`/users?search=${encodeURIComponent(clientSearch)}&role=direct_client`);
                if (!cancelled) {
                    setClients(data || []);
                    setShowClientDropdown(true);
                }
            } catch (err) {
                console.error('Failed to load clients:', err);
            } finally {
                if (!cancelled) setSearchingClients(false);
            }
        };
        const timer = setTimeout(fetchClients, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [clientSearch]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
                setShowClientDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setError('');
    };

    const handleSelectClient = (client) => {
        setSelectedClient(client);
        setClientSearch(client.full_name);
        setForm({
            ...form,
            client_id: client.id,
            client_name: client.full_name
        });
        setShowClientDropdown(false);
    };

    const handleClearClient = () => {
        setSelectedClient(null);
        setClientSearch('');
        setForm({ ...form, client_id: '', client_name: '' });
    };

    // Handle Add Client
    const handleAddClient = async (e) => {
        e.preventDefault();

        if (!newClient.full_name.trim() || !newClient.email.trim()) {
            setClientError('Client name and email are required');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newClient.email)) {
            setClientError('Please enter a valid email address');
            return;
        }

        setSavingClient(true);
        setClientError('');
        setClientSuccess('');

        try {
            const clientData = {
                full_name: newClient.full_name.trim(),
                email: newClient.email.trim(),
                phone: newClient.phone || null,
                company_name: newClient.company_name || null,
                kra_pin: newClient.kra_pin || null,
                pin: newClient.pin || null,
                role: 'direct_client',
                password: newClient.password || 'Temp123!@#',
            };

            await api.post('/auth/register', clientData);

            const { data: updatedClients } = await api.get(`/users?search=${newClient.full_name}&role=direct_client`);
            setClients(updatedClients);

            const newClientObj = updatedClients.find(c =>
                c.email === clientData.email ||
                c.full_name === clientData.full_name
            );

            if (newClientObj) {
                handleSelectClient(newClientObj);
                setClientSuccess(`Client "${newClient.full_name}" created and selected successfully!`);
            } else if (updatedClients.length > 0) {
                handleSelectClient(updatedClients[0]);
                setClientSuccess('Client created and selected successfully!');
            } else {
                setClientSuccess('Client created successfully! Please search and select them.');
            }

            setTimeout(() => {
                setShowAddClientModal(false);
                setClientSuccess('');
                setNewClient({
                    full_name: '',
                    email: '',
                    phone: '',
                    company_name: '',
                    kra_pin: '',
                    pin: '',
                    role: 'direct_client',
                    password: 'Temp123!@#',
                });
            }, 2000);

        } catch (err) {
            console.error('Add client error:', err);
            setClientError(err.response?.data?.error || err.response?.data?.message || 'Failed to add client');
        } finally {
            setSavingClient(false);
        }
    };

    const handleNewClientChange = (e) => {
        const { name, value } = e.target;
        setNewClient(prev => ({ ...prev, [name]: value }));
    };

    // Calculate end date (1 year from start)
    const getEndDate = () => {
        if (!form.start_date) return null;
        const start = new Date(form.start_date);
        const end = new Date(start);
        end.setFullYear(end.getFullYear() + 1);
        end.setDate(end.getDate() - 1);
        return end;
    };

    const endDate = getEndDate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (!form.client_id) {
            setError('Please select a client from the dropdown. Open covers must be linked to a client so any agent or broker can quote under them.');
            setLoading(false);
            return;
        }

        if (!user?.id) {
            setError('User not authenticated. Please log in again.');
            setLoading(false);
            return;
        }

        try {
            const uid = Number(user.id);
            const safeUid = Number.isFinite(uid) ? uid : user.id;
            const endDateStr = endDate ? endDate.toISOString().split('T')[0] : '';

            const submitData = {
                client_name: form.client_name || '',
                client_id: parseInt(form.client_id),
                sum_insured_limit: parseFloat(form.sum_insured_limit) || 0,
                currency: form.currency || 'USD',
                marine_rate: parseFloat(form.marine_rate) || 0.20,
                war_rate: parseFloat(form.war_rate) || 0.05,
                start_date: form.start_date || '',
                end_date: endDateStr,
                terms_conditions: form.terms_conditions || '',
                intermediary_id: safeUid,
                created_by_role: user?.role === 'agent' ? 'agent' : user?.role === 'broker' ? 'broker' : 'agent',
                created_by: safeUid,
                broker_id: user?.role === 'broker' ? safeUid : null,
                status: 'active',
                is_renewable: 1,
                renewal_count: 0,
                used_amount: 0,
                quote_count: 0,
                period_start: form.start_date || '',
                period_end: endDateStr,
            };

            await api.post('/quotes/open-covers', submitData);
            setSuccess('Open cover created successfully!');
            setTimeout(() => navigate('/open-covers'), 1500);
        } catch (err) {
            console.error('Create open cover error:', err);
            setError(err.response?.data?.error || err.response?.data?.message || 'Failed to create open cover');
        } finally {
            setLoading(false);
        }
    };

    const today = new Date().toISOString().split('T')[0];

    // Permission check
    if (
        user?.role !== 'agent' &&
        user?.role !== 'broker' &&
        user?.role !== 'admin' &&
        user?.role !== 'underwriter'
    ) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-emerald-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-emerald-950/30 flex items-center justify-center px-4">
                <div className="text-center max-w-md p-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 dark:border-slate-800">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/30 dark:to-rose-900/20 rounded-2xl flex items-center justify-center mb-5 shadow-inner">
                        <Lock className="w-10 h-10 text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Access Restricted</h3>
                    <p className="text-gray-500 dark:text-slate-400 text-sm leading-relaxed">
                        Only agents, brokers, underwriters, and admins can create annual open covers.
                    </p>
                    <Link
                        to="/open-covers"
                        className="inline-flex items-center gap-2 mt-7 px-6 py-3 bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white rounded-xl font-semibold shadow-lg shadow-emerald-900/20 transition-all active:scale-95"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Open Covers
                    </Link>
                </div>
            </div>
        );
    }

    const fmtCurrency = (v) =>
        new Intl.NumberFormat('en-KE', { maximumFractionDigits: 0 }).format(v || 0);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-emerald-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-emerald-950/30">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Navigation */}
                <div className="flex items-center justify-between print:hidden">
                    <button
                        onClick={() => navigate('/open-covers')}
                        className="group inline-flex items-center gap-2 text-gray-500 hover:text-[#3a663f] dark:text-slate-400 dark:hover:text-emerald-400 transition-colors font-medium text-sm"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                        Back to Open Covers
                    </button>
                    <div className="hidden sm:flex items-center gap-2 text-xs text-gray-400 dark:text-slate-500 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Draft in progress
                    </div>
                </div>

                {/* Hero Header */}
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#14291a] via-[#1e3b29] to-[#3a663f] p-8 md:p-10 text-white shadow-2xl shadow-emerald-950/20">
                    <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl" />
                    <div className="absolute -bottom-32 -left-16 w-96 h-96 bg-emerald-300/10 rounded-full blur-3xl" />
                    <div
                        className="absolute inset-0 opacity-[0.07]"
                        style={{
                            backgroundImage:
                                'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                            backgroundSize: '24px 24px',
                        }}
                    />

                    <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div className="max-w-2xl">
                            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/15">
                                <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
                                <span className="text-emerald-100 text-[11px] uppercase tracking-[0.15em] font-semibold">
                                    New Annual Open Cover
                                </span>
                            </div>
                            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                                Create Annual Open Cover
                            </h1>
                            <p className="text-emerald-100/80 mt-3 text-sm md:text-base leading-relaxed">
                                Establish a yearly marine cover agreement for your client. Once active,
                                any agent or broker quoting for this client can attach a cargo
                                certificate automatically — with instant approval.
                            </p>
                        </div>
                        <div className="flex flex-col items-start md:items-end gap-2">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/15">
                                <Award className="w-4 h-4 text-emerald-300" />
                                <span className="text-sm font-medium">12-Month Coverage</span>
                            </div>
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/15">
                                <TrendingUp className="w-4 h-4 text-emerald-300" />
                                <span className="text-sm font-medium">Auto-Approved Quotes</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Error / Success */}
                    {error && (
                        <AlertBanner type="error" onDismiss={() => setError('')}>
                            {error}
                        </AlertBanner>
                    )}
                    {success && <AlertBanner type="success">{success}</AlertBanner>}

                    {/* Intermediary Info */}
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/20 border border-blue-100 dark:border-blue-900/50 p-5">
                        <div className="flex items-start gap-4">
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
                                <User className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-blue-900 dark:text-blue-200 text-sm uppercase tracking-wider">
                                    Intermediary
                                </p>
                                <p className="text-blue-800 dark:text-blue-300 mt-1 font-medium flex items-center gap-2 flex-wrap">
                                    {user?.full_name || user?.username || 'Unknown'}
                                    <span className="text-[10px] uppercase tracking-wider bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-bold">
                                        {user?.role || 'agent'}
                                    </span>
                                </p>
                                <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-1.5">
                                    This open cover will be associated with your account. Any authorised
                                    user quoting for the linked client can select it.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Annual Info Banner */}
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border border-emerald-100 dark:border-emerald-900/50 p-5">
                        <div className="flex items-start gap-4">
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 flex-shrink-0">
                                <Calendar className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-emerald-900 dark:text-emerald-200 text-sm uppercase tracking-wider">
                                    Annual Cover Period
                                </p>
                                {form.start_date ? (
                                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                        <InfoStat
                                            label="Start Date"
                                            value={new Date(form.start_date).toLocaleDateString('en-KE', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                            })}
                                        />
                                        <InfoStat
                                            label="Expiry Date"
                                            value={
                                                endDate?.toLocaleDateString('en-KE', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                }) || '—'
                                            }
                                        />
                                        <div className="sm:col-span-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-emerald-700/80 dark:text-emerald-300/70 pt-1">
                                            <span className="inline-flex items-center gap-1">
                                                <CheckCircle className="w-3.5 h-3.5" /> 365-day coverage
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <CheckCircle className="w-3.5 h-3.5" /> Renewable upon expiry
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <CheckCircle className="w-3.5 h-3.5" /> Individual quotes ≤ 90 days
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-emerald-700/70 dark:text-emerald-300/70 mt-1.5">
                                        Pick a start date to see the full annual cover period.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Section: Client */}
                    <SectionCard icon={Users} title="Client Information" subtitle="Required — links this cover to a client" defaultOpen>
                        <div ref={searchWrapperRef} className="relative">
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300">
                                    Select Client <span className="text-red-500">*</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setShowAddClientModal(true)}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3a663f] dark:text-emerald-400 hover:text-[#2d5232] dark:hover:text-emerald-300 px-3 py-1.5 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    Add New Client
                                </button>
                            </div>

                            {selectedClient ? (
                                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border border-emerald-200 dark:border-emerald-900/50 rounded-2xl animate-in fade-in slide-in-from-top-1">
                                    <div className="flex items-center gap-4 min-w-0">
                                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-emerald-900/20 flex-shrink-0">
                                            {selectedClient.full_name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-gray-900 dark:text-white truncate">
                                                {selectedClient.full_name}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                                {selectedClient.email}
                                            </p>
                                            {selectedClient.company_name && (
                                                <p className="text-xs text-gray-400 dark:text-slate-500 truncate">
                                                    {selectedClient.company_name}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleClearClient}
                                        className="ml-3 text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30"
                                        aria-label="Clear client"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="relative">
                                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                                        <input
                                            type="text"
                                            value={clientSearch}
                                            onChange={(e) => setClientSearch(e.target.value)}
                                            onFocus={() => clients.length > 0 && setShowClientDropdown(true)}
                                            placeholder="Search client by name or email…"
                                            className="w-full pl-12 pr-4 py-3.5 border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 text-sm"
                                        />
                                        {searchingClients && (
                                            <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 animate-spin" />
                                        )}
                                    </div>

                                    {showClientDropdown && (
                                        <div className="absolute z-30 mt-2 w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 max-h-72 overflow-y-auto">
                                            {clients.length === 0 && !searchingClients && (
                                                <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-slate-400">
                                                    No clients found for "{clientSearch}"
                                                </div>
                                            )}
                                            {clients.map((client) => (
                                                <button
                                                    key={client.id}
                                                    type="button"
                                                    onClick={() => handleSelectClient(client)}
                                                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50/60 dark:hover:bg-emerald-950/20 transition-colors text-left border-b border-gray-100 dark:border-slate-700/60 last:border-0"
                                                >
                                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                                                        {client.full_name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                            {client.full_name}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                                            {client.email}
                                                        </p>
                                                        {client.company_name && (
                                                            <p className="text-xs text-gray-400 dark:text-slate-500 truncate">
                                                                {client.company_name}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </SectionCard>

                    {/* Section: Cover Details */}
                    <SectionCard icon={FileText} title="Cover Details" subtitle="Annual limit, currency and premium rates" defaultOpen>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                                    Annual Carry Limit <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 font-semibold text-sm pointer-events-none">
                                        {form.currency === 'USD' ? '$' : 'KSh'}
                                    </span>
                                    <input
                                        type="number"
                                        name="sum_insured_limit"
                                        value={form.sum_insured_limit}
                                        onChange={handleChange}
                                        required
                                        placeholder="e.g., 5000000"
                                        className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl pl-12 pr-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 text-sm"
                                    />
                                </div>
                                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1.5">
                                    Maximum total value across all quotes on this cover.
                                    {form.sum_insured_limit
                                        ? ` Currently ${form.currency === 'USD' ? '$' : 'KSh'} ${fmtCurrency(form.sum_insured_limit)}.`
                                        : ''}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                                    Currency <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="currency"
                                    value={form.currency}
                                    onChange={handleChange}
                                    className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 text-sm font-medium"
                                >
                                    <option value="USD">USD ($) — US Dollar</option>
                                    <option value="KES">KES (KSh) — Kenyan Shilling</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                                    Start Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="start_date"
                                    value={form.start_date}
                                    onChange={handleChange}
                                    min={today}
                                    required
                                    className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 text-sm"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                                        <Percent className="w-3.5 h-3.5 text-emerald-600" />
                                        Marine Rate
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            name="marine_rate"
                                            value={form.marine_rate}
                                            onChange={handleChange}
                                            step="0.01"
                                            required
                                            className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl pl-4 pr-8 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 text-sm"
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium pointer-events-none">
                                            %
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                                        <Percent className="w-3.5 h-3.5 text-emerald-600" />
                                        War Rate
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            name="war_rate"
                                            value={form.war_rate}
                                            onChange={handleChange}
                                            step="0.01"
                                            required
                                            className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl pl-4 pr-8 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 text-sm"
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium pointer-events-none">
                                            %
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SectionCard>

                    {/* Section: Terms */}
                    <SectionCard icon={BookOpen} title="Terms & Conditions" subtitle="Applies to every quote under this cover">
                        <div>
                            <textarea
                                name="terms_conditions"
                                value={form.terms_conditions}
                                onChange={handleChange}
                                rows={5}
                                placeholder="Enter the terms and conditions for this open cover agreement…"
                                className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 resize-none text-sm leading-relaxed"
                            />
                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1.5">
                                These clauses will be printed on every cargo certificate issued under this cover.
                            </p>
                        </div>
                    </SectionCard>

                    {/* Policy Information */}
                    <div className="rounded-2xl bg-gradient-to-br from-gray-50 to-emerald-50/50 dark:from-slate-900/60 dark:to-emerald-950/20 border border-gray-200 dark:border-slate-800 p-5">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3a663f] to-[#4d8c54] flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-900/20">
                                <Info className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="font-bold text-gray-800 dark:text-slate-200 text-sm">
                                    Open Cover Policy
                                </p>
                                <ul className="mt-3 space-y-2 text-sm text-gray-600 dark:text-slate-400">
                                    {[
                                        <>
                                            Valid for <strong className="text-gray-800 dark:text-slate-200">one year (365 days)</strong> from the start date.
                                        </>,
                                        <>
                                            Individual cargo quotes cannot exceed <strong className="text-gray-800 dark:text-slate-200">90 days</strong>.
                                        </>,
                                        'The combined value of active quotes cannot exceed the annual carry limit.',
                                        <>
                                            Upon expiry, the cover can be <strong className="text-gray-800 dark:text-slate-200">renewed</strong> by an underwriter or admin.
                                        </>,
                                        'Any agent or broker quoting for the linked client can select this cover — and get instant approval.',
                                    ].map((text, i) => (
                                        <li key={i} className="flex items-start gap-2.5">
                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                                            <span>{text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="sticky bottom-4 z-10">
                        <button
                            type="submit"
                            disabled={loading}
                            className="group w-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-4 rounded-2xl shadow-xl shadow-emerald-900/25 hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-2.5 transform active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Creating Annual Open Cover…
                                </>
                            ) : (
                                <>
                                    <Shield className="w-5 h-5 group-hover:rotate-3 transition-transform" />
                                    Create Annual Open Cover
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Add Client Modal */}
                {showAddClientModal && (
                    <AddClientModal
                        show={showAddClientModal}
                        onClose={() => {
                            setShowAddClientModal(false);
                            setClientError('');
                            setClientSuccess('');
                        }}
                        onSave={handleAddClient}
                        newClient={newClient}
                        onClientChange={handleNewClientChange}
                        saving={savingClient}
                        error={clientError}
                        success={clientSuccess}
                    />
                )}
            </div>
        </div>
    );
}

/* =========================================================
   REUSABLE COMPONENTS
   ========================================================= */

function AlertBanner({ type, children, onDismiss }) {
    const isError = type === 'error';
    return (
        <div
            className={`flex items-start gap-3 p-4 rounded-2xl border text-sm animate-in fade-in slide-in-from-top-1 ${isError
                    ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-300'
                    : 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-300'
                }`}
        >
            {isError ? (
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            ) : (
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            )}
            <span className="flex-1 leading-relaxed">{children}</span>
            {onDismiss && (
                <button
                    type="button"
                    onClick={onDismiss}
                    className={`p-1 rounded-lg transition-colors ${isError
                            ? 'hover:bg-red-100 dark:hover:bg-red-900/40 text-red-500'
                            : 'hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-emerald-500'
                        }`}
                    aria-label="Dismiss"
                >
                    <X className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

function InfoStat({ label, value }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-wider text-emerald-600/80 dark:text-emerald-400/80 font-bold">
                {label}
            </p>
            <p className="font-semibold text-emerald-900 dark:text-emerald-200 mt-0.5">
                {value}
            </p>
        </div>
    );
}

function SectionCard({ icon: Icon, title, subtitle, children, defaultOpen = true }) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm hover:shadow-md border border-gray-200 dark:border-slate-800 overflow-hidden transition-shadow">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-5 md:p-6 hover:bg-gray-50/70 dark:hover:bg-slate-800/50 transition-colors text-left"
            >
                <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-950/50 dark:to-teal-950/30 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#3a663f] dark:text-emerald-400" />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 dark:text-white">{title}</h3>
                        {subtitle && (
                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                                {subtitle}
                            </p>
                        )}
                    </div>
                </div>
                <svg
                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isOpen && (
                <div className="px-5 md:px-6 pb-6 pt-0 border-t border-gray-100 dark:border-slate-800">
                    <div className="pt-5">{children}</div>
                </div>
            )}
        </div>
    );
}

function AddClientModal({ show, onClose, onSave, newClient, onClientChange, saving, error, success }) {
    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-8 border border-white/20 dark:border-slate-800">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-950/50 dark:to-teal-950/30 flex items-center justify-center">
                            <User className="w-5 h-5 text-[#3a663f] dark:text-emerald-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Add New Client
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                                Creates a new direct-client account
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {error && <AlertBanner type="error">{error}</AlertBanner>}
                {success && <AlertBanner type="success">{success}</AlertBanner>}

                <form onSubmit={onSave} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ModalInput
                            label="Full Name *"
                            name="full_name"
                            value={newClient.full_name}
                            onChange={onClientChange}
                            placeholder="e.g., John Doe"
                            required
                        />
                        <ModalInput
                            label="Email *"
                            name="email"
                            type="email"
                            value={newClient.email}
                            onChange={onClientChange}
                            placeholder="e.g., client@example.com"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ModalInput
                            label="Phone"
                            name="phone"
                            value={newClient.phone}
                            onChange={onClientChange}
                            placeholder="e.g., +254 712 345 678"
                        />
                        <ModalInput
                            label="Company Name"
                            name="company_name"
                            value={newClient.company_name}
                            onChange={onClientChange}
                            placeholder="e.g., ABC Trading Ltd"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ModalInput
                            label="KRA PIN"
                            name="kra_pin"
                            value={newClient.kra_pin}
                            onChange={onClientChange}
                            placeholder="e.g., P000600883U"
                            mono
                        />
                        <ModalInput
                            label="PIN (Portal)"
                            name="pin"
                            value={newClient.pin}
                            onChange={onClientChange}
                            placeholder="e.g., 123456"
                        />
                    </div>

                    <div>
                        <ModalInput
                            label="Default Password"
                            name="password"
                            value={newClient.password}
                            onChange={onClientChange}
                            placeholder="Default password for client login"
                        />
                        <p className="text-xs text-gray-400 dark:text-slate-500 mt-1.5">
                            The client should change this password after first login.
                        </p>
                    </div>

                    <div className="flex gap-3 pt-5 border-t border-gray-100 dark:border-slate-800">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 px-4 border border-gray-200 dark:border-slate-700 rounded-2xl text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors font-semibold text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 py-3 px-4 bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white rounded-2xl font-semibold transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20 text-sm"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Creating…
                                </>
                            ) : (
                                <>
                                    <User className="w-4 h-4" />
                                    Create Client
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ModalInput({ label, name, value, onChange, placeholder, type = 'text', mono = false, required = false }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">
                {label}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                required={required}
                className={`w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 text-sm ${mono ? 'font-mono' : ''
                    }`}
            />
        </div>
    );
}